import React, { useCallback } from 'react';
import { Button } from '../atoms/button';
import { LoginForm } from '../molecules/LoginForm';

/**
 * LoginTemplate Props
 *
 * Template component for the login form.
 * Pure presentation - no business logic.
 * Uses the reusable LoginForm component from AuthDialog.
 */
export interface LoginTemplateProps {
  /** Callback for email/password login */
  onLogin: (email: string, password: string) => Promise<void>;

  /** Callback for Google OAuth login */
  onGoogleAuth: () => Promise<void>;

  /** Whether the form is submitting */
  isLoading?: boolean;

  /** Whether this is the web platform (affects sizing) */
  isWeb?: boolean;

  /** Optional callback to navigate to register page */
  onNavigateToRegister?: () => void;

  /** Optional callback to navigate to forgot password page */
  onNavigateToForgotPassword?: () => void;

  /** Initial error message to display */
  initialError?: string;
}

/**
 * LoginTemplate
 *
 * Login form template that reuses UI and logic from AuthDialog.
 * Now uses the extracted LoginForm component for consistency.
 *
 * Features:
 * - Email/password login (via LoginForm)
 * - Google OAuth login
 * - Form validation (via LoginForm)
 * - Link to register page
 * - Link to forgot password page
 *
 * Architecture:
 * - LoginForm: Handles email/password form UI and validation
 * - LoginTemplate: Adds Google auth button and navigation links
 *
 * Usage:
 * ```tsx
 * <LoginTemplate
 *   onLogin={handleLogin}
 *   onGoogleAuth={handleGoogleAuth}
 *   onNavigateToRegister={() => navigate('/register')}
 *   onNavigateToForgotPassword={() => navigate('/forgot-password')}
 *   isLoading={isLoading}
 *   isWeb={true}
 * />
 * ```
 */
export const LoginTemplate: React.FC<LoginTemplateProps> = ({
  onLogin,
  onGoogleAuth,
  isLoading = false,
  isWeb = true,
  onNavigateToRegister,
  onNavigateToForgotPassword,
  initialError,
}) => {
  const handleGoogleAuth = useCallback(async () => {
    if (isLoading) return;
    await onGoogleAuth();
  }, [onGoogleAuth, isLoading]);

  return (
    <div className="w-full">
      {/* Page Title */}
      <h1 className="text-3xl font-semibold text-primary-900 dark:text-gray-100 text-center mb-2">Hi 👋</h1>
      <p className="text-[#535862] dark:text-gray-400 text-center mb-8">Sign in to start managing your startup.</p>

      {/* Reusable Login Form with Google button */}
      <LoginForm
        onLogin={onLogin}
        onGoogleAuth={onGoogleAuth}
        isLoading={isLoading}
        isWeb={isWeb}
        initialError={initialError}
        showForgotPassword={!!onNavigateToForgotPassword}
        onForgotPassword={onNavigateToForgotPassword}
      />

      {/* Link to Register */}
      <div className="flex items-center justify-center mt-6">
        <p className="text-sm text-gray-600 font-normal dark:text-gray-400">
          Don't have an account?{' '}
          {onNavigateToRegister ? (
            <button
              type="button"
              onClick={onNavigateToRegister}
              className="text-primary-600 hover:text-primary-700 font-semibold dark:text-primary-400 dark:hover:text-primary-300"
              disabled={isLoading}
              data-testid="btn-navigate-to-register"
            >
              Sign up
            </button>
          ) : (
            <a
              href="/register"
              className="text-primary-600 hover:text-primary-700 font-semibold dark:text-primary-400 dark:hover:text-primary-300"
              data-testid="link-to-register"
            >
              Sign up
            </a>
          )}
        </p>
      </div>
    </div>
  );
};
